import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Play, Eye, Search, Loader2, Info, ExternalLink, LayoutDashboard, List } from 'lucide-react';
import { transcriptionService } from '../services/transcriptionService';
import TranscriptionDashboard from '../components/TranscriptionDashboard';
import QAFloatingButton from '../components/QAFloatingButton';

const Transcriptions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [transcriptResult, setTranscriptResult] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Form states
  const [newTranscription, setNewTranscription] = useState({
    companyName: '',
    quarter: 'Q1',
    date: '',
  });

  const [statusCheck, setStatusCheck] = useState({
    jobId: '',
  });

  const [quickView, setQuickView] = useState({
    jobId: '',
  });

  const [generatedSearch, setGeneratedSearch] = useState({
    companyName: '',
    quarter: 'Q1',
    date: '',
  });

  const [formErrors, setFormErrors] = useState({});

  // View mode toggle - NEW
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' or 'manual'

  // Clear error when form changes
  const handleFormChange = (formType, field, value) => {
    if (formType === 'new') {
      setNewTranscription(prev => ({ ...prev, [field]: value }));
    } else if (formType === 'status') {
      setStatusCheck(prev => ({ ...prev, [field]: value }));
    } else if (formType === 'view') {
      setQuickView(prev => ({ ...prev, [field]: value }));
    } else if (formType === 'search') {
      setGeneratedSearch(prev => ({ ...prev, [field]: value }));
    }

    // Clear errors for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleStartTranscription = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Convert date format for backend (YYYY-MM-DD to YYYY_MM_DD)
    const formattedData = {
      ...newTranscription,
      date: newTranscription.date ? newTranscription.date.replace(/-/g, '_') : ''
    };

    // Validate form
    const validation = transcriptionService.validateTranscriptionData(formattedData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setLoading(true);
    try {
      const result = await transcriptionService.startTranscription(formattedData);

      if (result.status === 'success' || result.job_id) {
        setSuccess(`Transcription started successfully! Job ID: ${result.job_id}`);
        setNewTranscription({ companyName: '', quarter: 'Q1', date: '' });
        setFormErrors({});
      } else {
        setError(result.message || 'Failed to start transcription');
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    setError(null);
    setJobStatus(null);

    if (!statusCheck.jobId.trim()) {
      setFormErrors({ jobId: 'Job ID is required' });
      return;
    }

    setStatusLoading(true);
    try {
      const result = await transcriptionService.getTranscriptionStatus(statusCheck.jobId);
      setJobStatus(result);
      setFormErrors({});
    } catch (err) {
      setError(err);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleQuickView = async (e, isFullView = false) => {
    e.preventDefault();
    setError(null);

    if (!quickView.jobId.trim()) {
      setFormErrors({ quickViewJobId: 'Job ID is required' });
      return;
    }

    setLoading(true);
    try {
      let result;
      if (isFullView) {
        // For full view, get more detailed chunks
        result = await transcriptionService.getTranscriptionChunks(quickView.jobId);
      } else {
        // For preview, get the basic result
        result = await transcriptionService.getTranscriptionResult(quickView.jobId);
      }

      setTranscriptResult({ ...result, isFullView });
      setShowModal(true);
      setFormErrors({});
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratedSearch = async (e) => {
    e.preventDefault();
    setError(null);

    // Convert date format for backend (YYYY-MM-DD to YYYY_MM_DD)
    const formattedSearchData = {
      ...generatedSearch,
      date: generatedSearch.date ? generatedSearch.date.replace(/-/g, '_') : ''
    };

    const validation = transcriptionService.validateTranscriptionData(formattedSearchData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setLoading(true);
    try {
      const result = await transcriptionService.getGeneratedTranscript(formattedSearchData);

      // If we have a job_id in the result, navigate to the full transcript page
      if (result && result.job_id) {
        const jobId = String(result.job_id);
        console.log('Navigating to transcript viewer with jobId:', jobId);
        navigate(`/transcript/${jobId}`);
      } else {
        console.log('No job_id found, showing modal with result:', result);
        // Fallback to modal if no job_id available
        setTranscriptResult(result);
        setShowModal(true);
      }
      setFormErrors({});
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div className="container" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Transcription Management</h1>
            <p className="dashboard-subtitle">Initiate transcription jobs, monitor their progress, and view results</p>
          </div>

          {/* View Mode Toggle - NEW */}
          <div className="flex items-center space-x-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('dashboard')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'dashboard'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <LayoutDashboard className="h-4 w-4" />
                <span>Dashboard View</span>
              </button>
              <button
                onClick={() => setViewMode('manual')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'manual'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="h-4 w-4" />
                <span>Transcribe</span>
              </button>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="error-container">
            <div className="error-title">Error</div>
            <div className="error-message">
              {typeof error === 'string' ? error : error.message || 'An unexpected error occurred'}
            </div>
          </div>
        )}

        {success && (
          <div className="success-container">
            <div className="success-message">{success}</div>
          </div>
        )}

        {/* Conditional Content Based on View Mode */}
        {viewMode === 'dashboard' ? (
          <TranscriptionDashboard />
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              {/* Top Row: Initiate New Transcription and Check Job Status */}
              <div className="grid grid-cols-2" style={{ gap: '32px' }}>
                {/* Initiate New Transcription */}
                <div className="form-section">
                  <h2 className="form-title">Initiate New Transcription</h2>
                  <form onSubmit={handleStartTranscription}>
                    <div style={{ marginBottom: '24px' }}>
                      <div className="form-group" style={{ marginBottom: '20px' }}>
                        <label className="label">Company Name *</label>
                        <input
                          type="text"
                          value={newTranscription.companyName}
                          onChange={(e) => handleFormChange('new', 'companyName', e.target.value)}
                          placeholder="Enter company name"
                          className={`input ${formErrors.companyName ? 'error' : ''}`}
                        />
                        {formErrors.companyName && (
                          <div className="error-text">{formErrors.companyName}</div>
                        )}
                      </div>

                      <div className="form-group" style={{ marginBottom: '20px' }}>
                        <label className="label">Quarter *</label>
                        <select
                          value={newTranscription.quarter}
                          onChange={(e) => handleFormChange('new', 'quarter', e.target.value)}
                          className={`select ${formErrors.quarter ? 'error' : ''}`}
                        >
                          <option value="Q1">Q1</option>
                          <option value="Q2">Q2</option>
                          <option value="Q3">Q3</option>
                          <option value="Q4">Q4</option>
                        </select>
                        {formErrors.quarter && (
                          <div className="error-text">{formErrors.quarter}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="label">Date *</label>
                        <input
                          type="date"
                          value={newTranscription.date}
                          onChange={(e) => handleFormChange('new', 'date', e.target.value)}
                          placeholder="dd/mm/yyyy"
                          className={`input ${formErrors.date ? 'error' : ''}`}
                        />
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                          Date will be automatically formatted for the backend (YYYY_MM_DD)
                        </div>
                        {formErrors.date && (
                          <div className="error-text">{formErrors.date}</div>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                    >
                      {loading ? (
                        <>
                          <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                          <span>Starting...</span>
                        </>
                      ) : (
                        <>
                          <Play style={{ width: '16px', height: '16px' }} />
                          <span>Start Transcription</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>

                {/* Check Job Status */}
                <div className="form-section">
                  <h2 className="form-title">Check Job Status</h2>
                  <form onSubmit={handleCheckStatus}>
                    <div style={{ marginBottom: '24px' }}>
                      <div className="form-group">
                        <label className="label">Job ID *</label>
                        <input
                          type="text"
                          value={statusCheck.jobId}
                          onChange={(e) => handleFormChange('status', 'jobId', e.target.value)}
                          placeholder="Enter Job ID (e.g., job_20250904021373972326)"
                          className={`input ${formErrors.jobId ? 'error' : ''}`}
                        />
                        {formErrors.jobId && (
                          <div className="error-text">{formErrors.jobId}</div>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={statusLoading}
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                    >
                      {statusLoading ? (
                        <>
                          <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                          <span>Checking...</span>
                        </>
                      ) : (
                        <>
                          <Search style={{ width: '16px', height: '16px' }} />
                          <span>Check Status</span>
                        </>
                      )}
                    </button>
                  </form>

                  {/* Job Status Result */}
                  {jobStatus && (
                    <div style={{
                      marginTop: '24px',
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <h4 style={{ fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                        Job Status
                      </h4>
                      <div style={{ fontSize: '14px', color: '#64748b' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span>Status:</span>
                          <span style={{
                            fontWeight: '500',
                            color: jobStatus.status === 'completed' ? '#059669' : '#3b82f6'
                          }}>
                            {jobStatus.status || 'Processing'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span>Progress:</span>
                          <span style={{ fontWeight: '500', color: '#1e293b' }}>
                            {(() => {
                              // Debug: log the full jobStatus to see what fields are available
                              console.log('Job Status Object:', jobStatus);

                              // PRIORITY 1: Check for chunk information first (more informative than percentages)
                              if (jobStatus.completed_chunks && jobStatus.total_chunks) {
                                return `${jobStatus.completed_chunks}/${jobStatus.total_chunks} chunks`;
                              }
                              if (jobStatus.chunks_processed && jobStatus.total_chunks) {
                                return `${jobStatus.chunks_processed}/${jobStatus.total_chunks} chunks`;
                              }
                              if (jobStatus.processed_chunks && jobStatus.total_chunks) {
                                return `${jobStatus.processed_chunks}/${jobStatus.total_chunks} chunks`;
                              }

                              // Check if chunks array exists and count completed ones
                              if (jobStatus.chunks && Array.isArray(jobStatus.chunks)) {
                                const completedChunks = jobStatus.chunks.filter(chunk =>
                                  chunk.status === 'completed' || chunk.processed === true
                                ).length;
                                return `${completedChunks}/${jobStatus.chunks.length} chunks`;
                              }

                              // Extract chunk info from message if available
                              if (jobStatus.message && typeof jobStatus.message === 'string') {
                                // Look for patterns like "Returning 8 processed chunks" or "8/12 chunks"
                                const chunkMatch = jobStatus.message.match(/(\d+)\s+processed\s+chunks/i);
                                if (chunkMatch) {
                                  const processedCount = chunkMatch[1];
                                  // Try to find total from other fields or estimate
                                  const totalChunks = jobStatus.total_chunks ||
                                    jobStatus.expected_chunks ||
                                    (jobStatus.status === 'completed' ? processedCount : '?');
                                  return `${processedCount}/${totalChunks} chunks`;
                                }

                                // Look for direct chunk ratios in message
                                const ratioMatch = jobStatus.message.match(/(\d+)\/(\d+)\s*chunks?/i);
                                if (ratioMatch) {
                                  return `${ratioMatch[1]}/${ratioMatch[2]} chunks`;
                                }
                              }

                              // PRIORITY 2: Try direct progress fields (but chunks are preferred)
                              if (jobStatus.progress && typeof jobStatus.progress === 'string' && jobStatus.progress.includes('chunks')) {
                                return jobStatus.progress;
                              }

                              // Check for any field that might indicate chunk progress
                              const allKeys = Object.keys(jobStatus);
                              const chunkKeys = allKeys.filter(key =>
                                key.toLowerCase().includes('chunk')
                              );

                              if (chunkKeys.length > 0) {
                                console.log('Found potential chunk keys:', chunkKeys);
                                // Try to extract meaningful chunk info
                                for (const key of chunkKeys) {
                                  const value = jobStatus[key];
                                  if (typeof value === 'string' && value.includes('/')) {
                                    return `${value} chunks`;
                                  }
                                  if (typeof value === 'number' && value > 0 && value < 1000) {
                                    // Assume it's a chunk count
                                    return `${value}/? chunks`;
                                  }
                                }
                              }

                              // PRIORITY 3: Fall back to percentages only if no chunk info available
                              if (jobStatus.progress_percentage) return `${jobStatus.progress_percentage}%`;
                              if (jobStatus.percentage) return `${jobStatus.percentage}%`;
                              if (jobStatus.progress && typeof jobStatus.progress === 'string') return jobStatus.progress;

                              // Status-based fallbacks
                              if (jobStatus.status === 'running') {
                                return 'Processing...';
                              }
                              if (jobStatus.status === 'completed') {
                                return 'Complete';
                              }

                              // Last resort - show all available fields for debugging
                              console.log('Available fields in jobStatus:', Object.keys(jobStatus));
                              return 'In Progress...';
                            })()}
                          </span>
                        </div>
                        {/* Progress Bar */}
                        {(() => {
                          let progressPercent = 0;
                          let showProgressBar = false;

                          // PRIORITY 1: Calculate progress from chunk information
                          if (jobStatus.completed_chunks && jobStatus.total_chunks) {
                            progressPercent = Math.round((jobStatus.completed_chunks / jobStatus.total_chunks) * 100);
                            showProgressBar = true;
                          } else if (jobStatus.chunks_processed && jobStatus.total_chunks) {
                            progressPercent = Math.round((jobStatus.chunks_processed / jobStatus.total_chunks) * 100);
                            showProgressBar = true;
                          } else if (jobStatus.processed_chunks && jobStatus.total_chunks) {
                            progressPercent = Math.round((jobStatus.processed_chunks / jobStatus.total_chunks) * 100);
                            showProgressBar = true;
                          } else if (jobStatus.chunks && Array.isArray(jobStatus.chunks)) {
                            const completedChunks = jobStatus.chunks.filter(chunk =>
                              chunk.status === 'completed' || chunk.processed === true
                            ).length;
                            progressPercent = Math.round((completedChunks / jobStatus.chunks.length) * 100);
                            showProgressBar = true;
                          } else if (jobStatus.message && typeof jobStatus.message === 'string') {
                            // Try to extract chunk progress from message
                            const chunkMatch = jobStatus.message.match(/(\d+)\s+processed\s+chunks/i);
                            if (chunkMatch && jobStatus.total_chunks) {
                              progressPercent = Math.round((parseInt(chunkMatch[1]) / jobStatus.total_chunks) * 100);
                              showProgressBar = true;
                            } else if (chunkMatch && jobStatus.status === 'completed') {
                              // If completed, assume this is 100%
                              progressPercent = 100;
                              showProgressBar = true;
                            }
                          }

                          // PRIORITY 2: Fall back to percentage fields only if no chunk info
                          if (!showProgressBar) {
                            if (jobStatus.progress_percentage) {
                              progressPercent = jobStatus.progress_percentage;
                              showProgressBar = true;
                            } else if (jobStatus.percentage) {
                              progressPercent = jobStatus.percentage;
                              showProgressBar = true;
                            } else if (jobStatus.status === 'completed') {
                              progressPercent = 100;
                              showProgressBar = true;
                            }
                          }

                          if (jobStatus.status === 'running' && !showProgressBar) {
                            // Show indeterminate progress for running jobs without specific progress
                            return (
                              <div style={{ marginTop: '12px' }}>
                                <div style={{
                                  width: '100%',
                                  height: '6px',
                                  background: '#e2e8f0',
                                  borderRadius: '3px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: '30%',
                                    height: '100%',
                                    background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                                    borderRadius: '3px',
                                    animation: 'progress-slide 2s ease-in-out infinite'
                                  }}></div>
                                </div>
                                <style>
                                  {`
                                @keyframes progress-slide {
                                  0% { transform: translateX(-100%); }
                                  50% { transform: translateX(250%); }
                                  100% { transform: translateX(-100%); }
                                }
                              `}
                                </style>
                              </div>
                            );
                          }

                          if (showProgressBar) {
                            return (
                              <div style={{ marginTop: '12px' }}>
                                <div style={{
                                  width: '100%',
                                  height: '6px',
                                  background: '#e2e8f0',
                                  borderRadius: '3px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${Math.min(progressPercent, 100)}%`,
                                    height: '100%',
                                    background: progressPercent === 100 ? '#10b981' : '#3b82f6',
                                    borderRadius: '3px',
                                    transition: 'width 0.3s ease'
                                  }}></div>
                                </div>
                              </div>
                            );
                          }

                          return null;
                        })()}

                        {jobStatus.message && (
                          <div style={{ marginTop: '8px', fontSize: '13px', fontStyle: 'italic' }}>
                            {(() => {
                              // Make the message more contextual based on status
                              let message = jobStatus.message;

                              // Remove confusing "more may be coming" for completed jobs
                              if (jobStatus.status === 'completed' && message.includes('more may be coming')) {
                                message = message.replace(/\s*\(more may be coming\)/gi, '');
                                message = message.replace(/,\s*more may be coming/gi, '');
                                // If it's completed, make it clear
                                if (message.includes('processed chunks')) {
                                  message = message.replace('Returning', 'Processing completed with');
                                }
                              }

                              // For running jobs, keep the original message as it's informative
                              if (jobStatus.status === 'running' && !message.includes('in progress') && !message.includes('processing')) {
                                // Add clarifying context for running jobs
                                if (message.includes('processed chunks')) {
                                  message = message + ' (processing in progress)';
                                }
                              }

                              return message;
                            })()}
                          </div>
                        )}

                        {/* Temporary debug info - remove once we identify the correct fields */}
                        <details style={{ marginTop: '12px', fontSize: '12px', color: '#64748b' }}>
                          <summary style={{ cursor: 'pointer' }}>
                            🔍 Debug: View API Response Fields
                          </summary>
                          <pre style={{
                            marginTop: '8px',
                            padding: '8px',
                            background: '#f8fafc',
                            borderRadius: '4px',
                            fontSize: '11px',
                            overflow: 'auto',
                            maxHeight: '150px'
                          }}>
                            {JSON.stringify(jobStatus, null, 2)}
                          </pre>
                        </details>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick View Transcript */}
              <div className="form-section">
                <h2 className="form-title">Quick View Transcript</h2>
                <form onSubmit={handleQuickView}>
                  <div style={{ marginBottom: '24px' }}>
                    <div className="form-group">
                      <input
                        type="text"
                        value={quickView.jobId}
                        onChange={(e) => handleFormChange('view', 'jobId', e.target.value)}
                        placeholder="Enter Job ID to view transcript"
                        className={`input ${formErrors.quickViewJobId ? 'error' : ''}`}
                      />
                      {formErrors.quickViewJobId && (
                        <div className="error-text">{formErrors.quickViewJobId}</div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-secondary"
                      style={{ flex: 1 }}
                    >
                      <Eye style={{ width: '16px', height: '16px' }} />
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (quickView.jobId.trim()) {
                          const jobId = String(quickView.jobId.trim());
                          console.log('Full View button - navigating to:', jobId);
                          navigate(`/transcript/${jobId}`);
                        } else {
                          setFormErrors({ quickViewJobId: 'Job ID is required for Full View' });
                        }
                      }}
                      disabled={loading}
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                    >
                      <ExternalLink style={{ width: '16px', height: '16px' }} />
                      Full View
                    </button>
                  </div>
                </form>
              </div>

              {/* View Generated Transcripts */}
              <div className="form-section">
                <h2 className="form-title">View Generated Transcripts</h2>
                <div style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>
                  Search for previously generated transcripts by company, quarter, and date.
                </div>
                <form onSubmit={handleGeneratedSearch}>
                  <div style={{ marginBottom: '24px' }}>
                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <input
                        type="text"
                        value={generatedSearch.companyName}
                        onChange={(e) => handleFormChange('search', 'companyName', e.target.value)}
                        placeholder="Enter company name"
                        className={`input ${formErrors.companyName ? 'error' : ''}`}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <select
                        value={generatedSearch.quarter}
                        onChange={(e) => handleFormChange('search', 'quarter', e.target.value)}
                        className="select"
                      >
                        <option value="Q1">Q1</option>
                        <option value="Q2">Q2</option>
                        <option value="Q3">Q3</option>
                        <option value="Q4">Q4</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <input
                        type="date"
                        value={generatedSearch.date}
                        onChange={(e) => handleFormChange('search', 'date', e.target.value)}
                        className="input"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                  >
                    <FileText style={{ width: '16px', height: '16px' }} />
                    Find Transcript
                  </button>
                </form>
              </div>
            </div>

            {/* Information Box */}
            <div className="info-box">
              <div className="info-title">
                <Info style={{ width: '16px', height: '16px' }} />
                Transcription Information
              </div>
              <ul className="info-list">
                <li>Transcription jobs are processed asynchronously in the background</li>
                <li>Use the job status checker to monitor progress</li>
                <li>Completed transcripts can be viewed using the Quick View or Full View options</li>
                <li>Search for previously generated transcripts by company, quarter, and date</li>
                <li>Generate AI-powered call insights from completed transcriptions</li>
              </ul>
            </div>

            {/* Modal for viewing transcript */}
            {showModal && transcriptResult && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '32px',
                  maxWidth: '800px',
                  width: '100%',
                  maxHeight: '80vh',
                  overflow: 'auto'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b' }}>
                      {transcriptResult?.isFullView ? 'Full Transcript View' : 'Transcript Preview'}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {transcriptResult?.isFullView && (
                        <button
                          onClick={() => {
                            // Extract all transcript text and download as file
                            let textContent = '';
                            if (transcriptResult.records && Array.isArray(transcriptResult.records)) {
                              textContent = transcriptResult.records
                                .map(record => `Chunk ${record.chunk_index || 'N/A'}: ${record.file_name || 'Unknown'}\n${record.full_transcript_text || record.transcript || 'No transcript'}\n\n`)
                                .join('');
                            } else {
                              textContent = JSON.stringify(transcriptResult, null, 2);
                            }

                            const blob = new Blob([textContent], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `transcript_${transcriptResult.job_id || 'unknown'}.txt`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          }}
                          className="btn btn-secondary"
                          style={{ fontSize: '14px', padding: '8px 16px' }}
                        >
                          📄 Download
                        </button>
                      )}
                      <button
                        onClick={() => setShowModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '24px',
                          cursor: 'pointer',
                          color: '#64748b'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <div style={{
                    background: '#f8fafc',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    lineHeight: '1.6'
                  }}>
                    {(() => {
                      // Remove the isFullView flag before processing
                      const cleanResult = { ...transcriptResult };
                      delete cleanResult.isFullView;

                      // If it's a direct transcript string
                      if (typeof cleanResult === 'string') {
                        return cleanResult;
                      }

                      // Check for chunks array first (this is the format we see in the screenshot)
                      if (cleanResult.chunks && Array.isArray(cleanResult.chunks)) {
                        return (
                          <div>
                            <div style={{ marginBottom: '16px', padding: '12px', background: '#eff6ff', borderRadius: '6px' }}>
                              <strong>Job Status:</strong> {cleanResult.status || 'Completed'}<br />
                              <strong>Message:</strong> {(() => {
                                let message = cleanResult.message || 'Processing complete';
                                // Clean up confusing messages for display
                                if (message.includes('more may be coming')) {
                                  message = message.replace(/\s*\(more may be coming\)/gi, '');
                                  message = message.replace(/,\s*more may be coming/gi, '');
                                  if (message.includes('processed chunks')) {
                                    message = message.replace('Returning', 'Processing completed with');
                                  }
                                }
                                return message;
                              })()}<br />
                              <strong>Job ID:</strong> {cleanResult.job_id || 'N/A'}
                            </div>

                            <div style={{ fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                              {transcriptResult.isFullView ? 'Complete Transcript (All Chunks):' : 'Transcript Content:'}
                            </div>

                            {cleanResult.chunks.map((chunk, index) => (
                              <div key={index} style={{
                                marginBottom: '16px',
                                padding: '16px',
                                background: 'white',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                              }}>
                                <div style={{
                                  fontSize: '13px',
                                  color: '#64748b',
                                  marginBottom: '8px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  borderBottom: '1px solid #f1f5f9',
                                  paddingBottom: '8px'
                                }}>
                                  <span><strong>📁 {chunk.file_name || `audio_chunk_t1.wav`}</strong></span>
                                  {transcriptResult.isFullView && (
                                    <span style={{ fontSize: '11px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                      {chunk.full_transcript_text?.length || 0} chars
                                    </span>
                                  )}
                                </div>
                                <div style={{
                                  fontSize: '14px',
                                  lineHeight: '1.7',
                                  color: '#374151',
                                  textAlign: 'justify'
                                }}>
                                  {chunk.full_transcript_text || 'No transcript available for this chunk'}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      }

                      // If it has records array (alternative format)
                      if (cleanResult.records && Array.isArray(cleanResult.records)) {
                        return (
                          <div>
                            <div style={{ marginBottom: '16px', padding: '12px', background: '#eff6ff', borderRadius: '6px' }}>
                              <strong>Job Status:</strong> {cleanResult.status || 'Completed'}<br />
                              <strong>Message:</strong> {(() => {
                                let message = cleanResult.message || 'Processing complete';
                                // Clean up confusing messages for display
                                if (message.includes('more may be coming')) {
                                  message = message.replace(/\s*\(more may be coming\)/gi, '');
                                  message = message.replace(/,\s*more may be coming/gi, '');
                                  if (message.includes('processed chunks') && (cleanResult.status === 'completed' || !cleanResult.status)) {
                                    message = message.replace('Returning', 'Processing completed with');
                                  }
                                }
                                return message;
                              })()}<br />
                              <strong>Job ID:</strong> {cleanResult.job_id || 'N/A'}
                            </div>

                            <div style={{ fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                              {transcriptResult.isFullView ? 'Complete Transcript (All Records):' : 'Transcript Content:'}
                            </div>

                            {cleanResult.records.map((record, index) => (
                              <div key={index} style={{
                                marginBottom: '16px',
                                padding: '16px',
                                background: 'white',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                              }}>
                                <div style={{
                                  fontSize: '13px',
                                  color: '#64748b',
                                  marginBottom: '8px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  borderBottom: '1px solid #f1f5f9',
                                  paddingBottom: '8px'
                                }}>
                                  <span><strong>📁 Chunk {record.chunk_index || index + 1}:</strong> {record.file_name || `Segment ${index + 1}`}</span>
                                  {transcriptResult.isFullView && (
                                    <span style={{ fontSize: '11px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                      {(record.full_transcript_text || record.transcript)?.length || 0} chars
                                    </span>
                                  )}
                                </div>
                                <div style={{
                                  fontSize: '14px',
                                  lineHeight: '1.7',
                                  color: '#374151',
                                  textAlign: 'justify'
                                }}>
                                  {record.full_transcript_text || record.transcript || 'No transcript available'}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      }

                      // If it has a single transcript property
                      if (cleanResult.transcript) {
                        return (
                          <div>
                            <div style={{ marginBottom: '16px', padding: '12px', background: '#eff6ff', borderRadius: '6px' }}>
                              <strong>Job Status:</strong> {cleanResult.status || 'Completed'}<br />
                              <strong>Job ID:</strong> {cleanResult.job_id || 'N/A'}
                            </div>
                            <div style={{
                              fontSize: '14px',
                              lineHeight: '1.7',
                              color: '#374151',
                              textAlign: 'justify',
                              padding: '16px',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              {cleanResult.transcript}
                            </div>
                          </div>
                        );
                      }

                      // If it has content property
                      if (cleanResult.content) {
                        return (
                          <div style={{
                            fontSize: '14px',
                            lineHeight: '1.7',
                            color: '#374151',
                            textAlign: 'justify',
                            padding: '16px',
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                          }}>
                            {cleanResult.content}
                          </div>
                        );
                      }

                      // Fallback - try to extract any text content
                      if (cleanResult.result) {
                        if (typeof cleanResult.result === 'string') {
                          return (
                            <div style={{
                              fontSize: '14px',
                              lineHeight: '1.7',
                              color: '#374151',
                              textAlign: 'justify',
                              padding: '16px',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              {cleanResult.result}
                            </div>
                          );
                        }
                        if (cleanResult.result.transcript) {
                          return (
                            <div style={{
                              fontSize: '14px',
                              lineHeight: '1.7',
                              color: '#374151',
                              textAlign: 'justify',
                              padding: '16px',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              {cleanResult.result.transcript}
                            </div>
                          );
                        }
                      }

                      // Last resort - show user-friendly message instead of raw JSON
                      return (
                        <div>
                          <div style={{ marginBottom: '16px', padding: '12px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                            <strong style={{ color: '#92400e' }}>⚠️ Unexpected Response Format</strong><br />
                            <span style={{ color: '#92400e', fontSize: '14px' }}>
                              The transcript data is in an unexpected format. Please try again or contact support.
                            </span>
                          </div>
                          <details style={{ marginTop: '16px' }}>
                            <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: '12px', marginBottom: '8px' }}>
                              Click to view raw response (for debugging)
                            </summary>
                            <pre style={{
                              whiteSpace: 'pre-wrap',
                              fontSize: '11px',
                              background: '#f8fafc',
                              padding: '12px',
                              borderRadius: '6px',
                              border: '1px solid #e2e8f0',
                              color: '#64748b',
                              overflow: 'auto',
                              maxHeight: '200px'
                            }}>
                              {JSON.stringify(cleanResult, null, 2)}
                            </pre>
                          </details>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Q&A Floating Button */}
      <QAFloatingButton />
    </div>
  );
};

export default Transcriptions;